import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bike, Wrench, Package, AlertTriangle, ScanLine, Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-[#141414] border border-zinc-800 rounded-sm p-5 flex items-center gap-4">
      <div className={`h-11 w-11 rounded-sm flex items-center justify-center ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-display text-4xl leading-none">{value}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const [bikes, setBikes] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const s = await api.get("/stats");
      setStats(s.data);
    } catch (e) {}
    if (hasPerm("inventory.view")) {
      try {
        const b = await api.get("/bikes");
        setBikes(b.data);
      } catch (e) {}
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = bikes.filter((b) => b.numero.toLowerCase().includes(query.toLowerCase()));

  const goByNumber = (e) => {
    e.preventDefault();
    const num = query.trim();
    if (!num) return;
    const exact = bikes.find((b) => b.numero.toLowerCase() === num.toLowerCase());
    if (exact) navigate(`/bici/${exact.id}`);
    else toast.error(`Nessuna bici con numero "${num}"`);
  };

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Benvenuto, {user?.name?.split(" ")[0]}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">DASHBOARD</h1>
        </div>
        <button onClick={() => navigate("/scanner")} data-testid="scanner-btn"
          className="hidden sm:flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors active:scale-95">
          <ScanLine className="h-5 w-5" /> Scansiona QR
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          <StatCard testid="stat-bikes" icon={Bike} label="Bici" value={stats.bikes} accent="bg-[#f98125]/20 text-[#f98125]" />
          <StatCard testid="stat-maintenance" icon={Wrench} label="Interventi" value={stats.maintenance} accent="bg-[#39FF14]/15 text-[#39FF14]" />
          <StatCard testid="stat-parts" icon={Package} label="Ricambi" value={stats.parts} accent="bg-zinc-700/40 text-zinc-300" />
          <StatCard testid="stat-lowstock" icon={AlertTriangle} label="Sotto scorta" value={stats.low_stock} accent="bg-[#FF9500]/15 text-[#FF9500]" />
          <button onClick={() => hasPerm("reports.view") && navigate("/segnalazioni")} className="text-left">
            <StatCard testid="stat-reports" icon={AlertTriangle} label="Segnalazioni aperte" value={stats.open_reports ?? 0} accent="bg-[#FF3B30]/15 text-[#FF3B30]" />
          </button>
        </div>
      )}

      {hasPerm("reports.view") && stats?.open_reports > 0 && (
        <div onClick={() => navigate("/segnalazioni")} data-testid="open-reports-banner"
          className="mb-8 cursor-pointer bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] rounded-sm px-4 py-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4" /> {stats.open_reports} segnalazione/i aperta/e da gestire →
        </div>
      )}

      {hasPerm("users.manage") && stats?.pending_users > 0 && (
        <div onClick={() => navigate("/amministrazione")}
          className="mb-8 cursor-pointer bg-[#FF9500]/10 border border-[#FF9500]/40 text-[#FF9500] rounded-sm px-4 py-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4" /> {stats.pending_users} utente/i in attesa di approvazione →
        </div>
      )}

      {hasPerm("inventory.view") && (
        <>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-[#f98125]" /> Trova subito la bici
            </h2>
            {hasPerm("inventory.create") && (
              <button onClick={() => navigate("/inventario?nuova=1")} data-testid="quick-add-bike"
                className="flex items-center gap-1.5 text-sm text-[#f98125] hover:underline">
                <Plus className="h-4 w-4" /> Aggiungi numero bici
              </button>
            )}
          </div>

          <form onSubmit={goByNumber} className="relative mb-5 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
            <Input data-testid="bike-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Digita il numero della bici e premi invio..."
              className="pl-9 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
          </form>

          {filtered.length === 0 ? (
            <div className="text-zinc-600 text-sm py-10 text-center border border-dashed border-zinc-800 rounded-sm">
              {bikes.length === 0 ? "Nessuna bici registrata. Vai in Inventario per aggiungerne." : "Nessun risultato."}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3">
              {filtered.map((b) => (
                <button key={b.id} data-testid={`bike-tile-${b.numero}`} onClick={() => navigate(`/bici/${b.id}`)}
                  title={`${b.modello || ""} ${b.taglia || ""}`}
                  className="aspect-square flex flex-col items-center justify-center bg-[#141414] border border-zinc-800 rounded-sm hover:border-[#f98125] hover:bg-[#f98125]/10 transition-all group">
                  <span className="font-display text-2xl sm:text-3xl text-white group-hover:text-[#f98125] transition-colors">{b.numero}</span>
                  {b.maintenance_count > 0 && (
                    <span className="text-[9px] text-zinc-500 mt-0.5">{b.maintenance_count} int.</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
